import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MagnifyingGlass, ArrowRight, CheckCircle, Copy } from "@phosphor-icons/react";

const examples = [
  { label: "Hamburg · Gastronomie · Juni 2026", bundesland: "hamburg", landkreis: "", month: "2026-06", keyword: "gastronomie" },
  { label: "Bayern · Amberg · Gastronomie · März 2026", bundesland: "bayern", landkreis: "amberg", month: "2026-03", keyword: "gastronomie" },
  { label: "Brandenburg · Gastronomie · März 2026", bundesland: "brandenburg", landkreis: "", month: "2026-03", keyword: "gastronomie" },
];

export default function Recherche() {
  const [form, setForm] = useState({ bundesland: "hamburg", landkreis: "", month: "2026-06", keyword: "gastronomie" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const applyExample = (ex) => setForm({ bundesland: ex.bundesland, landkreis: ex.landkreis, month: ex.month, keyword: ex.keyword });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/scrape", { source: "startupservice", ...form });
      setResult(data);
      toast.success(`${data.new} neue Leads, ${data.duplicates} Duplikate übersprungen`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="font-heading text-4xl font-bold tracking-tight">Recherche</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Neugründungen aus startupservice.net per Such-Parameter abrufen. Duplikate werden automatisch erkannt.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <form
          onSubmit={submit}
          data-testid="scrape-form"
          className="lg:col-span-2 bg-white border border-zinc-200 rounded-md p-6"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Bundesland *</Label>
              <Input
                value={form.bundesland}
                onChange={set("bundesland")}
                required
                data-testid="input-bundesland"
                placeholder="z.B. hamburg"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Landkreis (optional)</Label>
              <Input
                value={form.landkreis}
                onChange={set("landkreis")}
                data-testid="input-landkreis"
                placeholder="z.B. amberg"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Monat *</Label>
              <Input
                type="month"
                value={form.month}
                onChange={set("month")}
                required
                data-testid="input-month"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Stichwort</Label>
              <Input
                value={form.keyword}
                onChange={set("keyword")}
                data-testid="input-keyword"
                placeholder="z.B. gastronomie"
                className="mt-1.5"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            data-testid="scrape-submit-button"
            className="mt-6 bg-[#002FA7] hover:bg-[#001D6E] text-white rounded-sm h-11 px-6"
          >
            <MagnifyingGlass size={16} weight="bold" className="mr-2" />
            {loading ? "Recherchiere…" : "Recherche starten"}
          </Button>
        </form>

        {/* Examples */}
        <div className="bg-white border border-zinc-200 rounded-md p-6">
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Beispiele</div>
          <div className="space-y-2">
            {examples.map((ex, i) => (
              <button
                key={i}
                onClick={() => applyExample(ex)}
                data-testid={`example-${i}`}
                className="w-full text-left text-sm px-3 py-2.5 rounded-sm border border-zinc-200 hover:border-[#002FA7] hover:bg-[#002FA7]/5 transition-colors duration-150 flex items-center justify-between group"
              >
                <span>{ex.label}</span>
                <ArrowRight size={14} className="text-zinc-300 group-hover:text-[#002FA7]" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Result */}
      {result && (
        <div className="mt-8" data-testid="scrape-result">
          <div className="flex flex-wrap gap-3 mb-4">
            <Stat label="Gefunden" value={result.found} />
            <Stat label="Neu hinzugefügt" value={result.new} accent />
            <Stat label="Duplikate übersprungen" value={result.duplicates} muted />
          </div>
          <div className="text-xs text-zinc-400 break-all mb-4">{result.url}</div>

          {result.leads.length > 0 ? (
            <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
                    <th className="px-4 py-3 font-medium">Firma</th>
                    <th className="px-4 py-3 font-medium">Marke</th>
                    <th className="px-4 py-3 font-medium">Adresse</th>
                    <th className="px-4 py-3 font-medium">Typ</th>
                    <th className="px-4 py-3 font-medium">Gegründet</th>
                  </tr>
                </thead>
                <tbody>
                  {result.leads.map((l) => (
                    <tr key={l.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                      <td className="px-4 py-3 font-medium">{l.name}</td>
                      <td className="px-4 py-3 text-zinc-600">{l.name_for_emails}</td>
                      <td className="px-4 py-3 text-zinc-600">{l.plz} {l.city}, {l.street}</td>
                      <td className="px-4 py-3">{l.firmentyp}</td>
                      <td className="px-4 py-3 text-zinc-600">{l.gruendungsdatum}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-md p-4 text-sm flex items-center gap-2">
              <Copy size={18} /> Keine neuen Leads – alle Treffer waren bereits in der Datenbank.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent, muted }) {
  return (
    <div
      className={`px-5 py-3 rounded-md border ${
        accent ? "border-[#002FA7] bg-[#002FA7]/5" : "border-zinc-200 bg-white"
      }`}
    >
      <div className={`font-heading text-2xl font-bold ${accent ? "text-[#002FA7]" : muted ? "text-zinc-400" : ""}`}>
        {value}
      </div>
      <div className="text-xs uppercase tracking-widest text-zinc-500">{label}</div>
    </div>
  );
}
